import { createReducer, reducerSwitch } from 'lib/redux/reducerUtils'
import { API_CALL_FAILURE, API_CALL_REQUEST, API_CALL_SUCCESS, CLEAR_LOYALTY_ENROLMENT } from 'actions/actionConstants'
import {
  USER_LOGOUT,
  ENROL_LOYALTY_ACCOUNT,
  FETCH_LOYALTY_ACCOUNT,
  FETCH_LOYALTY_ACCOUNT_ELIGIBILITY,
  FETCH_LOYALTY_POINTS_EARN_CALCULATION,
  SUBMIT_LOYALTY_POINTS_BURN_CALCULATION,
  FETCH_LOYALTY_CONFIG,
  FETCH_LOYALTY_ACCOUNT_ACTIVITY,
  FETCH_LOYALTY_ACCOUNT_PENDING_POINTS,
  FETCH_LATEST_LOYALTY_ACCOUNT_DETAILS,
} from 'actions/apiActionConstants'

export const INITIAL_LOYALTY_STATE: App.LoyaltyState = {
  enrolment: undefined,
  account: { initial: true },
  eligibilityTier: { initial: true },
  pointsEarnCalculations: {},
  pointsBurnCalculations: {},
  config: undefined,
  configError: undefined,
  fetchingConfig: false,
  accountActivity: { initial: true },
  accountPendingPoints: { initial: true },
}

const apiRequests = reducerSwitch<App.LoyaltyState>({
  [ENROL_LOYALTY_ACCOUNT]: () => ({
    account: {
      fetching: true as const,
      error: undefined,
    },
  }),
  [FETCH_LOYALTY_ACCOUNT_ACTIVITY]: () => ({
    accountActivity: {
      fetching: true as const,
      error: undefined,
    },
  }),
  [FETCH_LOYALTY_ACCOUNT_PENDING_POINTS]: () => ({
    accountPendingPoints: {
      fetching: true as const,
      error: undefined,
    },
  }),
  [FETCH_LOYALTY_ACCOUNT]: () => ({
    account: {
      fetching: true as const,
      error: undefined,
    },
  }),
  [FETCH_LOYALTY_ACCOUNT_ELIGIBILITY]: () => ({
    eligibilityTier: {
      fetching: true as const,
      error: undefined,
    },
  }),
  [FETCH_LOYALTY_POINTS_EARN_CALCULATION]: (state, action) => ({
    pointsEarnCalculations: {
      ...state.pointsEarnCalculations,
      [action.pointsEarnCalculationKey]: { fetching: true },
    },
  }),
  [SUBMIT_LOYALTY_POINTS_BURN_CALCULATION]: (state, action) => {
    return {
      pointsBurnCalculations: {
        ...state.pointsBurnCalculations,
        [action.pointsBurnCalculationKey]: { fetching: true },
      },
    }
  },
  [FETCH_LOYALTY_CONFIG]: () => {
    return {
      fetchingConfig: true,
    }
  },
})

const apiSuccesses = reducerSwitch<App.LoyaltyState>({
  [ENROL_LOYALTY_ACCOUNT]: (_state, action) => ({
    account: {
      data: action.data,
      fetching: false as const,
      error: undefined,
    },
    enrolment: action.enrolment,
  }),
  [FETCH_LOYALTY_ACCOUNT]: (_state, action) => ({
    account: {
      data: action.data,
      fetching: false as const,
      error: undefined,
    },
  }),
  [USER_LOGOUT]: () => INITIAL_LOYALTY_STATE,
  [FETCH_LOYALTY_ACCOUNT_ELIGIBILITY]: (_state, action) => ({
    eligibilityTier: {
      data: action.data,
      fetching: false as const,
      error: undefined,
    },
  }),
  [FETCH_LOYALTY_POINTS_EARN_CALCULATION]: (state, action) => ({
    pointsEarnCalculations: {
      ...state.pointsEarnCalculations,
      [action.pointsEarnCalculationKey]: { data: action.data },
    },
  }),
  [FETCH_LOYALTY_ACCOUNT_ACTIVITY]: (_state, action) => ({
    accountActivity: {
      data: action.data,
      fetching: false as const,
      error: undefined,
    },
  }),
  [FETCH_LOYALTY_ACCOUNT_PENDING_POINTS]: (_state, action) => ({
    accountPendingPoints: {
      data: action.data,
      fetching: false as const,
      error: undefined,
    },
  }),
  [SUBMIT_LOYALTY_POINTS_BURN_CALCULATION]: (state, action) => {
    return {
      pointsBurnCalculations: {
        ...state.pointsBurnCalculations,
        [action.pointsBurnCalculationKey]: { data: action.data },
      },
    }
  },
  [FETCH_LOYALTY_CONFIG]: (state, action) => {
    return {
      config: action.data,
      fetchingConfig: false,
    }
  },
  [FETCH_LATEST_LOYALTY_ACCOUNT_DETAILS]: (state, action) => {
    return {
      account: {
        data: action.data,
        fetching: false as const,
      },
    }
  },
})

const apiFailures = reducerSwitch<App.LoyaltyState>({
  [ENROL_LOYALTY_ACCOUNT]: (_state, action) => ({
    account: {
      fetching: false as const,
      error: action.error,
    },
  }),
  [FETCH_LOYALTY_ACCOUNT]: (_state, action) => ({
    account: {
      fetching: false as const,
      error: action.error,
    },
  }),
  [FETCH_LOYALTY_ACCOUNT_ELIGIBILITY]: (_state, action) => ({
    eligibilityTier: {
      fetching: false as const,
      error: action.error,
    },
  }),
  [FETCH_LOYALTY_POINTS_EARN_CALCULATION]: (state, action) => ({
    pointsEarnCalculations: {
      ...state.pointsEarnCalculations,
      [action.pointsEarnCalculationKey]: { error: action.error },
    },
  }),
  [SUBMIT_LOYALTY_POINTS_BURN_CALCULATION]: (state, action) => {
    return {
      pointsBurnCalculations: {
        ...state.pointsBurnCalculations,
        [action.pointsBurnCalculationKey]: { error: action.error },
      },
    }
  },
  [FETCH_LOYALTY_ACCOUNT_ACTIVITY]: (_state, action) => ({
    accountActivity: {
      fetching: false as const,
      error: action.error,
    },
  }),
  [FETCH_LOYALTY_ACCOUNT_PENDING_POINTS]: (_state, action) => ({
    accountPendingPoints: {
      fetching: false as const,
      error: action.error,
    },
  }),
  [FETCH_LOYALTY_CONFIG]: (state, action) => {
    return {
      configError: action.error,
      fetchingConfig: false,
    }
  },
})

export default createReducer<App.LoyaltyState>(INITIAL_LOYALTY_STATE, {
  [API_CALL_REQUEST]: (state, action) => apiRequests(action.api)(state, action),
  [API_CALL_FAILURE]: (state, action) => apiFailures(action.api)(state, action),
  [API_CALL_SUCCESS]: (state, action) => apiSuccesses(action.api)(state, action),
  [CLEAR_LOYALTY_ENROLMENT]: () => ({
    enrolment: undefined,
  }),
})
